import React, { useEffect, useState } from 'react';
import ScrollVelocity from '../blocks/TextAnimations/ScrollVelocity/ScrollVelocity';

interface LogoScrollerProps {
  className?: string;
}

interface LogoList {
  files: string[];
}

export default function LogoScroller({ className = '' }: LogoScrollerProps) {
  const [schoolLogos, setSchoolLogos] = useState<string[]>([]);
  const [museumLogos, setMuseumLogos] = useState<string[]>([]);
  const [techLogos, setTechLogos] = useState<string[]>([]);
  
  useEffect(() => {
    // Load logo lists from index.json files
    const fetchLogos = async () => {
      try {
        const schoolsResponse = await fetch('/images/logos/schools/index.json');
        const museumsResponse = await fetch('/images/logos/museums/index.json');
        const techResponse = await fetch('/images/logos/technical_institution/index.json');
        
        if (schoolsResponse.ok) {
          const data: LogoList = await schoolsResponse.json();
          setSchoolLogos(data.files.map(file => `/images/logos/schools/${file}`));
        }
        
        if (museumsResponse.ok) {
          const data: LogoList = await museumsResponse.json();
          setMuseumLogos(data.files.map(file => `/images/logos/museums/${file}`));
        }
        
        if (techResponse.ok) {
          const data: LogoList = await techResponse.json();
          setTechLogos(data.files.map(file => `/images/logos/technical_institution/${file}`));
        }
      } catch (error) {
        console.error('Error loading logo files:', error);
      }
    };
    
    fetchLogos();
  }, []);
  
  // Instead of HTML strings, create simple text entries with image paths
  // The ScrollVelocity component will use these to create image elements
  const schoolLogoTexts = schoolLogos.map(path => path);
  const institutionLogoTexts = [...museumLogos, ...techLogos].map(path => path);
  
  // Custom wrapper components to render inside ScrollVelocity
  const LogoImage = ({ text }: { text: string }) => (
    <img src={text} alt="Logo" className="scroller-logo" />
  );
  
  return (
    <div className={`logo-scroller ${className}`}>
      <h3 className="trust-heading">Trusted by universities and institutions across the world</h3>
      
      <div className="logo-scroller-container">
        {/* School logos scrolling from left to right */}
        {schoolLogoTexts.length > 0 && (
          <div className="logo-parallax">
            <div className="logo-scroll" style={{ animationDuration: '80s', animationDirection: 'normal' }}>
              {schoolLogoTexts.map((logo, index) => (
                <img key={index} src={logo} alt="School logo" className="scroller-logo" />
              ))}
              {/* Duplicate logos to ensure continuous flow */}
              {schoolLogoTexts.map((logo, index) => (
                <img key={`dup-${index}`} src={logo} alt="School logo" className="scroller-logo" />
              ))}
            </div>
          </div>
        )}
        
        {/* Museum and Tech institution logos scrolling from right to left */}
        {institutionLogoTexts.length > 0 && (
          <div className="logo-parallax">
            <div className="logo-scroll" style={{ animationDuration: '80s', animationDirection: 'reverse' }}>
              {institutionLogoTexts.map((logo, index) => (
                <img key={index} src={logo} alt="Institution logo" className="scroller-logo" />
              ))}
              {/* Duplicate logos to ensure continuous flow */}
              {institutionLogoTexts.map((logo, index) => (
                <img key={`dup-${index}`} src={logo} alt="Institution logo" className="scroller-logo" />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 